// The badge section of the member's own profile: the three pinned badges large
// and alive (or the three rarest held, when nothing is pinned), the private rank
// card, the way into the full collection, and the badges within reach (Next up).
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useGetMyBadgeRankQuery, useGetMyBadgesQuery } from "../../../services/rtkApi/badgesApi";
import { useAppSelector } from "../../../store/hooks";
import { BadgeArt } from "../art";
import type { HeldBadge } from "../types";
import BadgeDetailSheet from "../collection/BadgeDetailSheet";
import NextBadges from "../goals/NextBadges";
import { compareRarest, tierLine, tierLineText } from "../collection/badgeFormat";
import { TierName } from "../TierName";
import BadgeRankCard from "./BadgeRankCard";
import "./badgeShowcase.css";

/** The pinned badges in pin order, or the three rarest when nothing is pinned. */
export function pickShowcase(badges: HeldBadge[], pinned: readonly string[]): { items: HeldBadge[]; pinned: boolean } {
  const fromPins = pinned.map((code) => badges.find((b) => b.code === code)).filter((b): b is HeldBadge => !!b);
  if (fromPins.length) return { items: fromPins.slice(0, 3), pinned: true };
  return { items: [...badges].sort(compareRarest).slice(0, 3), pinned: false };
}

/** The line under the showcase. */
export function showcaseNote(shown: number, pinned: boolean): string {
  if (pinned) return "Pinned by you.";
  if (shown <= 1) return "Your only badge so far.";
  return `Your rarest ${shown === 2 ? "two" : "three"}. Pin your own from the collection.`;
}

const BadgeShowcase: React.FC = () => {
  const auth = useAppSelector((s) => s.auth);
  const ready = auth.bootstrapStatus === "ready" && !!auth.token;
  const { data, isLoading, isError } = useGetMyBadgesQuery(undefined, { skip: !ready });
  const { data: rank, isLoading: rankLoading } = useGetMyBadgeRankQuery(undefined, { skip: !ready });
  const [openCode, setOpenCode] = useState<string | null>(null);

  const badges = useMemo(() => data?.badges ?? [], [data]);
  const pins = useMemo(() => data?.pinned ?? [], [data]);
  const showcase = useMemo(() => pickShowcase(badges, pins), [badges, pins]);
  const openBadge = badges.find((b) => b.code === openCode) ?? null;

  if (!ready) return null;

  return (
    <section className="bshow" aria-label="Your badges">
      <div className="bshow__layout">
        <div className="bshow__featured">
          {isLoading ? (
            <p className="bshow__note">Loading your badges</p>
          ) : isError || !data ? (
            <p className="bshow__note">Your badges are not available right now.</p>
          ) : showcase.items.length === 0 ? (
            <p className="bshow__note">
              No badges yet. They come from taking part: make an AOTD, rate a coffee, join the morning race.
            </p>
          ) : (
            <>
              <div className="bshow__row">
                {showcase.items.map((b) => {
                  const line = tierLine({ tier: b.tier, holders: b.rarity?.holders ?? 0, members: b.rarity?.members ?? 0, unique: b.unique });
                  return (
                    <button
                      key={b.code}
                      type="button"
                      className="bshow__item"
                      onClick={() => setOpenCode(b.code)}
                      aria-label={`${b.name}. ${tierLineText(line)}${b.count > 1 ? ` Earned ${b.count} times.` : ""}`}
                    >
                      <span className="bshow__art">
                        <BadgeArt
                          category={b.category}
                          tier={b.tier}
                          number={b.number}
                          count={b.count}
                          code={b.code}
                          name={b.name}
                          size={112}
                          variant="full"
                          animate="idle"
                        />
                      </span>
                      <span className="bshow__name">{b.name}</span>
                      <TierName tier={b.tier} className="bshow__tier" />
                      {line.fact && <span className="bshow__fact">{line.fact}</span>}
                    </button>
                  );
                })}
              </div>
              <p className="bshow__note">
                {showcaseNote(showcase.items.length, showcase.pinned)}
              </p>
            </>
          )}
          <Link className="bshow__link" to="/badges">
            {badges.length ? `See all ${badges.length} in your collection` : "Open your collection"}
          </Link>
        </div>
        <BadgeRankCard rank={rank} loading={rankLoading} />
      </div>
      <NextBadges className="bshow__next" />
      <BadgeDetailSheet
        badge={openBadge}
        held
        mode="own"
        pinned={pins}
        open={!!openBadge}
        onOpenChange={(open) => {
          if (!open) setOpenCode(null);
        }}
      />
    </section>
  );
};

export default BadgeShowcase;
